import { GameDetails, GameDetailsTeam } from 'modules/games/domain/interfaces/game-details';
import type { MatchResolverContext, ResolverPlayer, ResolverTeam } from 'modules/manager/domain/placeholder';

export interface ReviewMatchContext {
  match: MatchResolverContext;
  /** Players of both teams by user id — the ids protocol events carry. */
  players: Map<number, ResolverPlayer>;
}

const toTeam = (team: GameDetailsTeam): ResolverTeam => ({
  id: team.id,
  name: team.name,
  // The protocol has no short name; the media manager's own render falls back to the full one too.
  shortName: team.name,
  logoUrl: team.logo || undefined,
});

const toPlayer = (member: GameDetailsTeam['members'][number]): ResolverPlayer => ({
  id: member.id,
  name: [member.firstName, member.lastName].filter(Boolean).join(' '),
  nameParts: { firstName: member.firstName || undefined, lastName: member.lastName || undefined },
  number: member.number,
  photoUrl: member.avatar || undefined,
});

/**
 * The match data the intro and plates are drawn from, built on the client from the match protocol — the backend
 * builds this context only for its own render. Championship and organizer logos are not in the protocol, so their
 * placeholders are dropped. Rosters stay empty: a plate fills them per goal (`buildPlateContext`), and an intro of
 * a match template does not show players.
 */
export function toReviewMatchContext(details: GameDetails): ReviewMatchContext {
  const [home, away] = details.teams;
  const players = new Map<number, ResolverPlayer>();
  details.teams.forEach((team) => team.members.forEach((member) => players.set(member.id, toPlayer(member))));

  return {
    match: {
      mode: 'match',
      tournament: { id: details.tournament.id, name: details.tournament.name },
      championship: { id: details.championship.id, name: details.championship.name },
      match: {
        id: details.id,
        date: details.date || undefined,
        venue: details.location?.name || undefined,
        homeScore: details.hasScore ? details.result.firstTeamScore : undefined,
        awayScore: details.hasScore ? details.result.secondTeamScore : undefined,
        roundName: details.round?.name,
      },
      homeTeam: toTeam(home),
      awayTeam: toTeam(away),
    },
    players,
  };
}
